import React, { useContext, useEffect } from "react"
import { connect } from "react-redux"
import { StyleSheet, View } from "react-native"
import { useNavigation } from "@react-navigation/native"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import * as amplitude from "@amplitude/analytics-react-native"
import { CongratsTemplate } from "@common/templates/CongratsTemplate"
import TextM from "@common/components/text/TextM"
import { theme } from "@common/theme/theme"
import { DimensionsContext, IS_WEB } from "@common/utils/mobileUtils"
import { apiRequest } from "@data/redux/actions/api"
import { useGetFastTrackUserOnMount } from "../fast-track/hooks/useGetFastTrackUserOnMount"
import DownloadAppButton from "../student/tutoring/DownloadAppButton"

const TRACKING_SOURCE = "parent_onboarding.download_app_cta"

const PARENT_BENEFITS = [
    {
        icon: "chart-line",
        text: "Suivez en temps réel les séances et les bilans de chaque cours",
    },
    {
        icon: "comments",
        text: "Échangez directement avec le tuteur sans passer par le support",
    },
    {
        icon: "bell",
        text: "Recevez les rappels avant chaque séance, plus jamais d'oubli",
    },
    {
        icon: "trophy",
        text: "Visualisez la progression et la régularité de votre enfant",
    },
]

const BenefitRow = ({ icon, text }) => (
    <View style={styles.benefitRow}>
        <View style={styles.benefitIcon}>
            <FontAwesome5
                name={icon}
                solid
                size={14}
                color={theme.colors.primary}
            />
        </View>
        <TextM
            fontSize={15}
            color={theme.colors.grey}
            style={styles.benefitText}
            wrap
        >
            {text}
        </TextM>
    </View>
)

const DownloadAppCtaScreen = (props) => {
    const navigation = useNavigation()
    const { initialLoading, user, emailB64 } = useGetFastTrackUserOnMount(props)
    const dimensions = useContext(DimensionsContext)

    // Belt and braces: the slot screen already guards this, but if we ever
    // end up here on a native build there is nothing useful to show.
    useEffect(() => {
        if (!IS_WEB) {
            navigation.navigate("subscribe", {
                user: emailB64,
                status: "success",
            })
            return
        }
        amplitude.track(`${TRACKING_SOURCE}.screen_view`)
    }, [emailB64])

    if (!IS_WEB) return null

    const childFirstname = user?.firstname

    return (
        <CongratsTemplate
            isLoading={initialLoading}
            error={props.errorRetrieveUser}
            title={
                childFirstname
                    ? `Bienvenue dans la famille Masteur, ${childFirstname} est prêt à démarrer`
                    : "Bienvenue dans la famille Masteur"
            }
            description="Une dernière étape pour profiter pleinement du suivi parent."
        >
            <View
                style={[
                    styles.container,
                    { width: Math.min(dimensions?.width ?? 600, 600) },
                ]}
            >
                <View style={styles.benefitsCard}>
                    {PARENT_BENEFITS.map((b) => (
                        <BenefitRow
                            key={b.icon}
                            icon={b.icon}
                            text={b.text}
                        />
                    ))}
                </View>

                <View style={styles.downloadBlock}>
                    <TextM
                        fontSize={18}
                        fontWeight="Bold"
                        color={theme.colors.primary}
                        style={styles.centered}
                        wrap
                    >
                        Téléchargez l'app parent
                    </TextM>
                    <DownloadAppButton trackingSource={TRACKING_SOURCE} />
                </View>
            </View>
        </CongratsTemplate>
    )
}

const styles = StyleSheet.create({
    container: {
        alignSelf: "center",
        gap: 30,
        paddingHorizontal: 10,
    },
    benefitsCard: {
        backgroundColor: theme.colors.white,
        padding: 20,
        borderRadius: 20,
        gap: 16,
        elevation: 2,
        shadowColor: theme.colors.black,
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.08,
        shadowRadius: 4,
    },
    benefitRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 12,
    },
    benefitIcon: {
        width: 32,
        height: 32,
        borderRadius: 8,
        backgroundColor:
            theme.colors.primaryLighter ?? theme.colors.linkLighter,
        alignItems: "center",
        justifyContent: "center",
    },
    benefitText: {
        flex: 1,
    },
    downloadBlock: {
        alignItems: "center",
        gap: 10,
    },
    centered: {
        textAlign: "center",
    },
})

const mapStateToProps = (state) => ({
    errorRetrieveUser: state.api.getSubscribePricing?.error,
})

const mapDispatchToProps = {
    apiRequest,
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(DownloadAppCtaScreen)
